import React, { useState, useEffect } from "react";
import lodash from "lodash";
import { useGetCall } from "seed/api";
import { LazyComponent } from "seed/helpers";
import * as Fields from "components/util/schema_form/fields";
import { HelpComponent } from "components/util/schema_form/util/help_util";
import SchemaHandler from "components/util/schema_form/SchemaForm.sub.SchemaHandler.view";


const InputComponent = ({
  input,
  appId,
  actionId,
  flowId,
  nodeId,
  parentId,
  executionId,
  sectionIdx,
  values,
  dataReqs,
  parentKey,
  isDeveloperMode,
  isReadOnly,
  isCommentEnabled,
  setFieldValue,
  setValues,
}) => {
  const name = parentKey == "" ? input.name : parentKey + "." + input.name;
  const type = input.type;
  const label = input.label ?? "";
  const value = lodash.get(values, name);
  const hidden = input?.hidden ?? false;
  const required = input?.required ?? !hidden;
  const disabled = input?.disabled ?? isReadOnly;
  const caption = input.caption ?? "";
  const helpMessage = input?.help_message;
  const placeholder = input?.placeholder;
  const multiple = input?.multiple ?? false;
  const enableComments = input?.enable_comments ?? isCommentEnabled;
  const defaultRawValue = input?.default;
  const [defaultValue, setDefaultValue] = useState(null);

  const developerOnly = input?.developer_only ?? false;

  const textType = input?.text_type ?? "general";
  const rows = input?.rows ?? 5;
  const options = input?.options ?? [];
  const optionsIcon = input?.options_icon;
  const dynamicOptions = input?.dynamic_options ?? {};
  const dynamicDependencies = input?.dynamic_dependencies ?? [];
  const multipleFiles = input?.multiple_files ?? false;
  const acceptFiles = input?.accept_files ?? "*";
  const acceptTypes = input?.accept_types ?? "all";
  const selectorFilters = input?.selector_filters ?? [];
  const supportIterables = input?.support_iterables ?? false;
  const enableAttributes = input?.enable_attributes ?? true;
  const integrationNames = input?.integration_names ?? [];
  const defaultAccountName = input?.default_account_name;
  const addAccountButtonText = input?.add_account_button_text;
  const schemaButtonText = input?.schema_button_text;
  const schemaFieldsPlaceholder = input?.schema_fields_placeholder;
  const schemaPreviewType = input?.schema_preview_type;
  const valueAAttrs = input?.value_a_attrs;
  const valueBAttrs = input?.value_b_attrs;
  const valueAPlaceholder = input?.value_a_placeholder
  const valueBPlaceholder = input?.value_b_placeholder
  const valueADefault = input?.value_a_default
  const valueBDefault = input?.value_b_default
  const conditionPlaceholder = input?.condition_placeholder
  const conditionDefault = input?.condition_default
  const showStyles = input?.show_styles ?? false;
  const showSaveAction = input?.show_save_action ?? false;
  const valueASource = input?.value_a_source
  const valueBSource = input?.value_b_source
  const valueAHeader = input?.value_a_header
  const valueBHeader = input?.value_b_header
  const valueAReference = input?.value_a_reference
  const valueBReference = input?.value_b_reference

  const openText = input?.open_text ?? false;
  const saveCollectionColumns = input?.save_collection_columns;
  const showCollectionCondition = input?.show_collection_condition ?? true;
  const showCollectionAddButton = input?.show_collection_add_button ?? false;
  const showConstants = input?.show_constants ?? false;
  const showNodeAddButton = input?.show_add_node_button ?? false;
  const displayAttributesManually = input?.display_attributes_manually ?? false;

  const fields = input?.fields ?? []
  const enableCollapse = input?.enable_collapse ?? false
  const collapsed = input?.collapsed ?? false

  const buttonType = input?.button_type ?? "CALL";
  const buttonAction = input?.button_action ?? "";
  const buttonText = input?.button_text ?? "";
  const buttonStyle = input?.button_style ?? "outline";
  const buttonCallback = input?.button_callback ?? (() => { });
  const buttonFile = input?.button_file ?? "";
  const labelText = input?.label_text ?? "";
  const labelFontStyle = input?.label_font_style ?? "normal"
  const labelFontSize = input?.label_font_size ?? "normal"

  // Select Field
  const selectType = input?.select_type ?? "dropdown";
  const multipleSelect = input?.multiple_select ?? false;

  // Confirm Field
  const confirmLabel = input?.confirm_label ?? "Confirmar";
  const rejectLabel = input?.reject_label ?? "Rechazar";

  // Phone Field
  const defaultCountry = input?.default_country ?? "mx";
  const allowedCountries = input?.allowed_countries ?? null;
  const allowedRegions = input?.allowed_regions ?? null;

  // Asset Field
  const assetType = input?.asset_type ?? "file";
  const assetValue = input?.asset_value;

  // Date and DateTime Field
  const minDate = input?.min ?? null;
  const maxDate = input?.max ?? null;

  // Hour Field
  const minHour = input?.min ?? null;
  const maxHour = input?.max ?? null;

  // Number Field
  const allowDecimals = input?.allow_decimals ?? false;
  const minValue = input?.min ?? null;
  const maxValue = input?.max ?? null;
  const step = input?.step ?? 1;

  // Location Field
  const buttonTextLocation = input?.button_text ?? "Usar mi ubicación";
  const buttonStyleLocation = input?.button_style ?? "filled";

  // Color Picker
  const openColor = input?.open_color ?? false;
  const colorOptions = input?.color_options ?? [];

  // Dashboard Builder
  const dashboardButtonText = input?.dashboard_button_text ?? "Configurar dashboard";

  // Table Field
  const dataSource = input?.data_source ?? null;
  const dataColumns = input?.data_columns ?? [];

  // Selector Field
  const dataValues = input?.data_values ?? [];
  const dataStructure = input?.data_structure ?? [];
  const dataMultiple = input?.data_multiple ?? false;

  // Data Viewer
  const dataName = input?.data_name ?? "";

  // Recursive select
  const optionsCall = input?.options_call ?? "";
  const selectCall = input?.select_call ?? "";

  // Smart Text
  const objective = input?.objective ?? "";
  const depth = input?.depth ?? 5;
  const examples = input?.examples;
  const hiddenId = "section-hidden-" + name.replaceAll("[", "").replaceAll("]", "").replaceAll(".", "-")

  // Excel
  const excelStructure = input?.excel_structure ?? [];
  const singleInput = input?.single_input ?? false;
  const templateUrl = input?.template_url ?? "";
  const showAdvancedOptions = input?.show_advanced_options ?? true;
  const excelOffset = input?.excel_offset;
  const excelSheet = input?.excel_sheet;

  // Collection
  const collectionButtonText = input?.collection_button_text;

  const [callRealValue, reqRealValue] = useGetCall("/executions/get_query_result", {
    "query": defaultRawValue,
    "flow_id": flowId,
    "execution_id": executionId,
  }, {
    onCompleted: (data) => {
      setDefaultValue(data);
    }
  })

  useEffect(() => {
    if (defaultRawValue && typeof defaultRawValue === "string") {
      if (defaultRawValue.startsWith("#") || defaultRawValue.startsWith("@"))
        callRealValue();
      else setDefaultValue(defaultRawValue)
    }
  }, []);

  if (developerOnly && !isDeveloperMode) return <></>;

  const commonProps = {
    name,
    label,
    type,
    value,
    required,
    hidden,
    disabled,
    multiple,
    caption,
    helpMessage,
    placeholder,
    enableComments,
    values,
    appId,
    actionId,
    flowId,
    nodeId,
    parentId,
    executionId,
    setFieldValue,
    input,
    sectionIdx,
    defaultValue,
  }

  
  const variableProps = {
    dataReqs,
    acceptTypes,
    openText,
    textType,
    selectorFilters,
    supportIterables,
    enableAttributes,
    showCollectionAddButton,
    showCollectionCondition,
    saveCollectionColumns,
    showConstants,
    showNodeAddButton,
    displayAttributesManually
  }

  return (
    (() => {

      switch (type) {

        case "TEXT":
          return (
            <Fields.TextField
              {...commonProps}
              textType={textType}
            />
          );

        case "TEXT_AREA":
          return (
            <Fields.TextAreaField
              {...commonProps}
              rows={rows}
            />
          );

        case "NUMBER":
          return (
            <Fields.NumberField
              {...commonProps}
              allowDecimals={allowDecimals}
              minValue={minValue}
              maxValue={maxValue}
              step={step}
            />
          );

        case "LOCATION":
          return (
            <LazyComponent
              {...commonProps}
              buttonText={buttonTextLocation}
              buttonStyle={buttonStyleLocation}
              component={Fields.LocationField}
            />
          );

        case "SELECT":
          return (
            dynamicOptions.source == null ?
              <Fields.SelectField
                {...commonProps}
                options={options}
                optionsIcon={optionsIcon}
                openText={openText}
                selectType={selectType}
                multiple={multipleSelect}
              /> :
              <Fields.DynamicSelectField
                {...commonProps}
                dynamicOptions={dynamicOptions}
                dynamicDependencies={dynamicDependencies}
                optionsIcon={optionsIcon}
                openText={openText}
                selectType={selectType}
                multiple={multipleSelect}
              />
          )

        case "CONFIRM":
          return (
            <Fields.ConfirmField
              {...commonProps}
              confirmLabel={confirmLabel}
              rejectLabel={rejectLabel}
            />
          );

        case "PHONE":
          return (
            <LazyComponent
              {...commonProps}
              defaultCountry={defaultCountry}
              allowedCountries={allowedCountries}
              allowedRegions={allowedRegions}
              component={Fields.PhoneField}
            />
          );

        case "DATE":
          return (
            <Fields.TextField
              {...commonProps}
              textType={"date"}
              minDate={minDate}
              maxDate={maxDate}
            />
          );

        case "DATE_TIME":
          return (
            <Fields.TextField
              {...commonProps}
              textType={"datetime-local"}
              minDate={minDate}
              maxDate={maxDate}
            />
          );

        case "HOUR":
          return (
            <LazyComponent
              {...commonProps}
              minHour={minHour}
              maxHour={maxHour}
              component={Fields.HourField}
            />
          );

        case "FILE":
          return (
            <Fields.FileField
              {...commonProps}
              acceptFiles={acceptFiles}
              multipleFiles={multipleFiles}
            />
          );

        case "CHECKBOX":
          return (
            <Fields.CheckboxField
              {...commonProps}
            />
          );

        case "CODE":
          return (
            <LazyComponent
              {...commonProps}
              component={Fields.CodeField}
            />
          );

        case "VARIABLE":
          return (
            <LazyComponent
              {...commonProps}
              {...variableProps}
              component={Fields.VariableField}
            />
          );

        case "SCHEMA_BUILDER":
          return (
            <LazyComponent
              {...commonProps}
              dataReqs={dataReqs}
              schemaButtonText={schemaButtonText}
              schemaFieldsPlaceholder={schemaFieldsPlaceholder}
              schemaPreviewType={schemaPreviewType}
              schema={value}
              component={Fields.SchemaBuilderField}
            />
          );

        case "DASHBOARD_BUILDER":
          return (
            <LazyComponent
              {...commonProps}
              dataReqs={dataReqs}
              dashboardButtonText={dashboardButtonText}
              component={Fields.DashboardBuilderField}
            />
          )

        case "MATCH":
          return (
            <LazyComponent
              {...commonProps}
              dataReqs={dataReqs}
              showSaveAction={showSaveAction}
              valueASource={valueASource}
              valueBSource={valueBSource}
              valueAPlaceholder={valueAPlaceholder}
              valueBPlaceholder={valueBPlaceholder}
              valueAHeader={valueAHeader}
              valueBHeader={valueBHeader}
              valueAReference={valueAReference}
              valueBReference={valueBReference}
              component={Fields.MatchField}
            />
          );

        case "ACCOUNT_PICKER":
          return (
            <LazyComponent
              {...commonProps}
              integrationNames={integrationNames}
              defaultAccountName={defaultAccountName}
              addAccountButtonText={addAccountButtonText}
              component={Fields.AccountPicker}
            />
          );

        case "LABEL":
          return (
            <Fields.LabelField
              {...commonProps}
              labelText={labelText}
              labelFontStyle={labelFontStyle}
              labelFontSize={labelFontSize}
            />
          );

        case "ASSET":
          return (
            <LazyComponent
              {...commonProps}
              assetType={assetType}
              assetValue={assetValue}
              component={Fields.AssetField}
            />
          );

        case "BUTTON":
          return (
            <Fields.ButtonField
              {...commonProps}
              buttonType={buttonType}
              buttonAction={buttonAction}
              buttonText={buttonText}
              buttonStyle={buttonStyle}
              buttonCallback={buttonCallback}
              buttonFile={buttonFile}
            />
          );

        case "RICH_TEXT":
          return (
            <LazyComponent
              {...commonProps}
              dataReqs={dataReqs}
              singleLine={true}
              showStyles={false}
              variableOptions={
                {...variableProps}
              }
              component={Fields.RichText}
            />
          )

        case "RICH_TEXT_AREA":
          return (
            <LazyComponent
              {...commonProps}
              dataReqs={dataReqs}
              singleLine={false}
              rows={rows}
              showStyles={showStyles}
              variableOptions={
                {...variableProps}
              }
              component={Fields.RichText}
            />
          )

        case "FILTER":
          return (
            <LazyComponent
              {...commonProps}
              dataReqs={dataReqs}
              valueAAttrs={valueAAttrs}
              valueBAttrs={valueBAttrs}
              valueAPlaceholder={valueAPlaceholder}
              valueBPlaceholder={valueBPlaceholder}
              valueADefault={valueADefault}
              valueBDefault={valueBDefault}
              conditionPlaceholder={conditionPlaceholder}
              conditionDefault={conditionDefault}
              component={Fields.FilterField}
            />
          )

        case "COLOR_PICKER":
          return (
            <Fields.ColorPickerField
              {...commonProps}
              openColor={openColor}
              colorOptions={colorOptions}
            />
          );

        case "DATA_VIEWER":
          return (
            <LazyComponent
              {...commonProps}
              dataName={dataName}
              component={Fields.DataViewerField}
            />
          );

        case "TABLE":
          return (
            <LazyComponent
              {...commonProps}
              dataSource={dataSource}
              dataColumns={dataColumns}
              component={Fields.TableField}
            />
          );

        case "SELECTOR":
          return (
            <LazyComponent
              {...commonProps}
              dataValues={dataValues}
              dataStructure={dataStructure}
              dataMultiple={dataMultiple}
              component={Fields.SelectorField}
            />
          );

        case "RECURSIVE_SELECT":
          return (
            <Fields.RecursiveSelectField
              {...commonProps}
              optionsCall={optionsCall}
              selectCall={selectCall}
            />
          )

        case "SMART_TEXT":
          return (
            <Fields.SmartTextField
              {...commonProps}
              objective={objective}
              depth={depth}
              examples={examples}
              dataReqs={dataReqs}
            />
          )

        case "EXCEL":
          return (
            <Fields.ExcelField
              {...commonProps}
              excelStructure={excelStructure}
              excelOffset={excelOffset}
              excelSheet={excelSheet}
              singleInput={singleInput}
              templateUrl={templateUrl}
              showAdvancedOptions={showAdvancedOptions}
            />
          )

        case "COLLECTION_BUILDER":
          return (
            <LazyComponent
              {...commonProps}
              schema={value}
              collectionButtonText={collectionButtonText}
              component={Fields.CollectionBuilderField}
            />
          );

        case "SECTION":
          return (
            <div className="w-100 mt-1">

              <a class="d-flex mb-1 w-100 justify-content-between" data-toggle="collapse" href={"#" + hiddenId}
                style={{ cursor: enableCollapse ? "pointer" : "default" }}>
                {label != "" ?
                  <span class="dropdown-header p-0">
                    <span style={{ color: "#777" }}>{label}</span>
                    {helpMessage != null ? <HelpComponent helpMessage={helpMessage} /> : null}
                  </span> : null}
                {enableCollapse ? <i style={{ color: "#aaa" }} class="ml-1 fas fa-chevron-down"></i> : null}
              </a>
              <hr class="mt-2 w-100" style={{ border: "none", height: "1.5px", background: "#e7eaf3" }} />
              <div className="mt-4">
                <div className={collapsed ? "collapse" : "show"} id={enableCollapse ? hiddenId : ""}>
                  <SchemaHandler schema={fields} appId={appId} actionId={actionId} flowId={flowId}
                    values={values} dataReqs={dataReqs} setFieldValue={setFieldValue} setValues={setValues}
                    isDeveloperMode={isDeveloperMode}
                    isReadOnly={isReadOnly} isCommentEnabled={isCommentEnabled}
                    parentKey={`${name}`} sectionIdx={sectionIdx}
                  />
                </div>
              </div>
            </div>
          );

        default:
          return <></>;
      }
    })())
}

export default InputComponent;